// src/utils/injectEditThemeStyles.js
export function injectEditThemeStyles(blockThemes = []) {
  if (typeof window === 'undefined') return;

  const EDIT_SCOPES = [
    '.block-editor-slate',
    '.block-editor',
    'body.cms-ui',
    'body.with-toolbar',
  ];

  const STYLE_ID = '__volto_edit_theme_preview__';
  if (!document.getElementById(STYLE_ID)) {
    // --- 1) CSS fallback: background-color per theme (so you see something even before JS sets vars) ---
    const resolveVar = (val) => {
      if (!val) return '';
      const m = String(val).match(/^var\((--[A-Za-z0-9-_]+)\)$/);
      if (!m) return val;
      const resolved = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
      return resolved || val;
    };

    const rules = [];
    blockThemes.forEach((t) => {
      const name = t?.name;
      const raw = t?.style?.['--theme-color'];
      if (!name || !raw) return;
      const colour = resolveVar(raw);
      EDIT_SCOPES.forEach((scope) => {
        rules.push(
          `${scope}[class*="has--background-color--${name}"] .inner,` +
          `${scope} .has--background-color--${name} .inner { background-color: ${colour} !important; }`,
        );
      });
    });

    const css = rules.join('\n');
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);

    // Debug
    // eslint-disable-next-line no-console
    console.log('[editThemeStyles] CSS injected');
  }

  // --- 2) Inline CSS variables on .inner (like View) ---
  const themeMap = new Map();
  const allVarKeys = new Set(); // track all possible --vars for clean-up

  blockThemes.forEach((t) => {
    if (t?.name && t?.style) {
      themeMap.set(t.name, t.style);
      Object.keys(t.style).forEach((k) => {
        if (k.startsWith('--')) allVarKeys.add(k);
      });
    }
  });

  const getThemeNameFromClasses = (el) => {
    const classes = el.className || '';
    const m = String(classes).match(/has--background-color--([A-Za-z0-9-_]+)/);
    return m ? m[1] : '';
  };

  const setInlineVars = (inner, styleObj) => {
    // clear previous theme vars first
    allVarKeys.forEach((k) => inner.style.removeProperty(k));
    // set new theme vars
    Object.entries(styleObj).forEach(([k, v]) => {
      if (k.startsWith('--')) inner.style.setProperty(k, v);
    });
  };

  const applyInlineVars = (root = document) => {
    const themedEls = root.querySelectorAll('[class*="has--background-color--"]');
    themedEls.forEach((el) => {
      const inner =
        el.querySelector('.inner') ||
        el.closest('.block')?.querySelector('.inner') ||
        null;
      if (!inner) return;

      const themeName = getThemeNameFromClasses(el);
      const styleObj = themeMap.get(themeName);
      if (!styleObj) return;

      setInlineVars(inner, styleObj);
    });
  };

  // Initial pass + observe edits
  const run = () => {
    // only act when edit scaffolding is present
    if (
      !document.querySelector('.block-editor-slate') &&
      !document.querySelector('.block-editor') &&
      !document.body.classList.contains('cms-ui') &&
      !document.body.classList.contains('with-toolbar')
    ) {
      return;
    }
    applyInlineVars(document);
  };

  run();

  // Coalesce updates via MutationObserver
  if (!window.__voltoEditThemeVarsObserver__) {
    const mo = new MutationObserver(() => run());
    mo.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    window.__voltoEditThemeVarsObserver__ = mo;
    // eslint-disable-next-line no-console
    console.log('[editThemeStyles] Inline vars enabled');
  }
}
